import React, { useState, useEffect, useRef } from 'react';
import './App.css';

/* ─── Constants ───────────────────────────────────────────────────────────────── */

const SOCIAL = {
  github:   'https://github.com/vivek-anand-singh',
  linkedin: 'https://www.linkedin.com/in/vivekanandsingh/',
  leetcode: 'https://leetcode.com/vivek-anand-singh/',
  email:    '17vivekanandsingh@gmail.com',
};

const TYPING_PHRASES = [
  'Backend Engineer',
  'Java Spring Boot Developer',
  'Distributed Systems Builder',
  'Microservices Architect',
];

const EXPERIENCES = [
  {
    company:  'Scaler AI Labs',
    role:     'Software Engineering Intern',
    ann:      '@TeamLead · @CodingRL · @MCPServers',
    period:   'Jan 2026 – May 2026',
    location: 'Bengaluru, Karnataka',
    tag:      'AI / RL',
    points: [
      'Led the CodingRL initiative as Team Lead — drove end-to-end project planning, scoped RL-relevant problem design, assigned tasks to fellow interns, and was the primary technical point-of-contact for the team throughout.',
      'Built CodingRL environments using real open-source GitHub repositories — authored authentic coding issues, implemented ground-truth resolutions, and designed automated judges to evaluate whether LLM agents could independently reproduce the fix; SWE-bench style training data built for frontier AI labs.',
      'Engineered MCP servers replicating real-world application interfaces — exposing standardized tool APIs for LLM agents to interact with in high-fidelity simulated environments.',
      'Contributed to a Data PII removal pipeline — identified and sanitized personally identifiable information from training datasets before ingestion into the RL pipeline.',
    ],
  },
  {
    company:  'Stealth AI Startup',
    role:     'Software Engineering Intern',
    ann:      '@FullOwnership · @BrowserExtension',
    period:   'July 2025 – Sept 2025',
    location: 'Bengaluru, Karnataka',
    tag:      'Full Stack / AI',
    points: [
      'Worked directly under the founder with full ownership of the consumer-facing product — a centralized AI memory platform that aggregates a user\'s scattered context from across multiple LLM interfaces into a single unified layer.',
      'Built the entire consumer side independently — designed and shipped dashboards, multiple REST APIs, backend integrations, and database management end-to-end with high ownership.',
      'Developed a browser extension that injected a custom UI into LLM platforms and — with explicit user consent — captured user prompts and conversation context across different AI tools, feeding them into the central memory store to make AI memory portable and persistent.',
    ],
  },
  {
    company:  'InterviewBit',
    role:     'Software Engineering Intern',
    ann:      '@ReferralProgram · @ABTesting · @AdminTools',
    period:   'April 2025 – July 2025',
    location: 'Bengaluru, Karnataka',
    tag:      'EdTech / SSB',
    points: [
      'Engineered the end-to-end Referral Program for Scaler School of Business — enabled paid users to refer prospects via a streamlined sharing flow, built a dashboard to track referrals and earnings, and implemented coupon application; achieved an 8% reduction in applicant acquisition cost.',
      'Built a second waitlist form for A/B testing against the existing one — captured user intent (name, email, phone, current education) with a redirect to the application funnel to optimize interest conversion.',
      'Developed an admin tool for the Sales team to extend application form deadlines for SSB — enabling sales reps to unblock applicants without requiring engineering intervention.',
      'Contributed to multiple features across the Scaler School of Business website, shipping product improvements across the full consumer stack.',
    ],
  },
  {
    company:  'InterviewBit',
    role:     'Subject Matter Expert (DSA)',
    ann:      '@Mentoring · @Teaching',
    period:   'Jan 2025 – April 2025',
    location: 'Remote',
    tag:      'DSA',
    points: [
      'Conducted ~8 one-on-one video mentoring sessions per day with students on Data Structures & Algorithms — covering graphs, dynamic programming, backtracking, segment trees, linked lists, and arrays.',
      'Received consistently strong student feedback, praised for clarity and the ability to break down complex problems intuitively — helping students build confidence for interviews and competitive programming.',
    ],
  },
];

const PROJECTS = [
  {
    name:    'InterviewExperiencePlatform',
    ann:     '@Microservices @SpringBoot',
    tech:    ['Java', 'Spring Boot', 'Apache Kafka', 'gRPC', 'PostgreSQL', 'JWT', 'Docker', 'RBAC', 'Swagger/OpenAPI'],
    points: [
      'Built a microservices backend (4 services: User, Content, Notification, API Gateway) containerized with Docker Compose; API Gateway centralizes JWT auth and RBAC.',
      'Implemented event-driven architecture using Apache Kafka for async messaging and gRPC for synchronous service-to-service communication.',
      'Developed threaded discussion system with comments, replies, company rooms, post tagging, flagging, and admin moderation.',
    ],
    live: 'https://sst-interview-frontend.vercel.app/',
  },
  {
    name:    'MSTeamsRLEnvironment',
    ann:     '@LLMTooling @MCPServer',
    tech:    ['Next.js', 'FastAPI', 'FastMCP', 'Docker', 'SQLAlchemy', 'Python'],
    points: [
      'Built a full-stack MS Teams simulation platform for training and evaluating LLM agents on multi-step workplace collaboration tasks.',
      'Developed a Codex-powered evaluation pipeline with 24 concurrent workers, generating 1,060+ tasks with automated graders.',
      'Designed an MCP server exposing 60+ Microsoft Graph-style tools for standardized agent interaction and reproducible benchmarking.',
    ],
  },
  {
    name:    'DistributedKeyValueCache',
    ann:     '@DistributedSystems @LowLatency',
    tech:    ['Python', 'asyncio', 'TCP Sockets', 'Docker', 'Consistent Hashing', 'LRU'],
    points: [
      'Built a 3-node distributed cache using consistent hashing and synchronous write-through replication ensuring data committed to both primary and replica shards.',
      'Implemented idempotent replication markers within a custom TCP protocol to prevent infinite forwarding cycles.',
      'Developed LRU eviction + lazy/active TTL-based expiration using an ordered hashmap for efficient memory management.',
    ],
    github: 'https://github.com/vivek-anand-singh/key-value-project',
  },
  {
    name:    'ELORE — Expert Learning Reasoning Engine',
    ann:     '@AIResearch @RAG @KnowledgeGraph',
    tech:    ['Python', 'FastAPI', 'LangGraph', 'ChromaDB', 'Neo4j', 'LangChain', 'SQLite', 'Gemini API'],
    points: [
      'AI system that learns from expert feedback to improve answers without model retraining — LangGraph state machine runs: retrieve → generate → gap analysis → learning event → improved answer.',
      'Built hybrid RAG pipeline combining ChromaDB (semantic vector search) and Neo4j (multi-hop knowledge graph traversal) for cross-domain entity relationships.',
      'Achieved 0.926 avg semantic similarity and +29.4% improvement in worst-performing questions after a single learning loop across 10 questions.',
    ],
    github: 'https://github.com/vivek-anand-singh/Expert-Learning-Organizational-Reasoning-Engine',
  },
  {
    name:    'UptimeMonitor',
    ann:     '@FullStack @DevOps',
    tech:    ['Node.js', 'Express.js', 'Docker', 'Docker Compose', 'axios'],
    points: [
      'Full-stack uptime monitoring app that periodically pings registered URLs and displays real-time operational status and response times on a live dashboard.',
      'Engineered a background ping service with scheduled health checks; status transitions (UP/DOWN) tracked with timestamps and HTTP status codes.',
      'Containerized with Docker Compose — frontend (Nginx) and backend (Node.js) deployed as independent services with a shared network.',
    ],
    github: 'https://github.com/vivek-anand-singh/uptime-monitor',
  },
  {
    name:    'DistributedLivePollingSystem',
    ann:     '@DistributedSystems @HighThroughput',
    tech:    ['Python', 'FastAPI', 'Redis', 'Consistent Hashing', 'asyncio', 'Docker'],
    points: [
      'High-throughput live polling backend with 2-node Redis sharding via a custom consistent hash ring using virtual nodes for uniform key distribution.',
      'Implemented batch vote processing — asyncio background worker buffers votes in memory and flushes to Redis on a configurable interval, reducing write amplification.',
      'Built TTL-based application-layer cache (5s) in front of Redis reads; served_via header provides cache-hit/miss observability per request.',
    ],
    github: 'https://github.com/vivek-anand-singh/distributed-live-polling-system-',
  },
];

const SKILLS = {
  Languages:          { icon: '☕', items: ['Java', 'Python', 'C++', 'JavaScript', 'Ruby'] },
  Frameworks:         { icon: '🍃', items: ['Spring Boot', 'Node.js', 'Express.js', 'FastAPI', 'Ruby on Rails', 'React', 'Next.js'] },
  Databases:          { icon: '🗄️', items: ['PostgreSQL', 'MySQL', 'MongoDB', 'SQLite'] },
  Infrastructure:     { icon: '🐳', items: ['Docker', 'Docker Compose', 'Git', 'GitHub', 'Linux'] },
  Architecture:       { icon: '⚙️', items: ['Microservices', 'Apache Kafka', 'gRPC', 'REST APIs', 'JWT', 'RBAC', 'HLD', 'LLD'] },
  DistributedSystems: { icon: '🌐', items: ['Consistent Hashing', 'Write-Through Replication', 'TCP Sockets', 'Low Latency', 'Schema Design'] },
  AIAndMLTooling:     { icon: '🤖', items: ['RL Environments', 'MCP Servers', 'LLM Toolchains', 'FastMCP', 'Reward Engineering'] },
  ProblemSolving:     { icon: '🔢', items: ['Data Structures', 'Algorithms', 'Competitive Programming', 'System Design'] },
};

const EDUCATION = [
  { institution: 'Scaler School of Technology', degree: 'Undergraduate Program in Computer Science', period: '2023 – 2027', score: 'CGR: 8.52',  loc: 'Bengaluru, Karnataka' },
  { institution: 'BITS Pilani',                 degree: 'B.S. (Hons.) in Computer Science',         period: '2023 – 2027', score: 'CGPA: 8.49', loc: 'Pilani, Rajasthan' },
];

const ACHIEVEMENTS = [
  { icon: '🏆', title: 'Meta RL Hackathon',    desc: 'Selected in Top 800 out of 31,000+ teams globally.' },
  { icon: '🎓', title: 'Academic Scholarship', desc: 'Awarded 25% tuition fee scholarship at Scaler School of Technology for excellent academic performance.' },
  { icon: '⚡', title: 'Open Source',          desc: 'Contributed to the PyMC GSoC repository.' },
  { icon: '🌿', title: 'Hacktoberfest 2024',   desc: 'Contributed to multiple open-source projects, focusing on frontend and backend improvements.' },
];

/* ─── Hooks ───────────────────────────────────────────────────────────────────── */

function useTyping(phrases) {
  const [text,     setText]     = useState('');
  const [pi,       setPi]       = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[pi];
    const speed   = deleting ? 35 : 75;
    const timer   = setTimeout(() => {
      if (!deleting) {
        if (text.length < current.length) {
          setText(current.slice(0, text.length + 1));
        } else {
          setTimeout(() => setDeleting(true), 2200);
        }
      } else {
        if (text.length > 0) {
          setText(text.slice(0, -1));
        } else {
          setDeleting(false);
          setPi((pi + 1) % phrases.length);
        }
      }
    }, speed);
    return () => clearTimeout(timer);
  }, [text, deleting, pi, phrases]);

  return text;
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    const el = ref.current;
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useScrollSpy(ids) {
  const [active, setActive] = useState('');
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: '-20% 0px -60% 0px' }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ids]);
  return active;
}

/* ─── Icons ───────────────────────────────────────────────────────────────────── */

const GitHubIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);
const LinkedInIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const LeetCodeIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z"/>
  </svg>
);
const EmailIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={size} height={size}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const ExtIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

/* ─── Syntax helpers for code blocks ─────────────────────────────────────────── */

const CLine = ({ children }) => <div className="code-line">{children || ' '}</div>;
const Kw    = ({ children }) => <span className="c-kw">{children}</span>;
const Ann   = ({ children }) => <span className="c-ann">{children}</span>;
const Cls   = ({ children }) => <span className="c-cls">{children}</span>;
const Fn    = ({ children }) => <span className="c-fn">{children}</span>;
const Str   = ({ children }) => <span className="c-str">{children}</span>;

/* ─── Navbar ──────────────────────────────────────────────────────────────────── */

const NAV_ITEMS = [
  { id: 'about',      label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects',   label: 'Projects' },
  { id: 'skills',     label: 'Skills' },
  { id: 'education',  label: 'Education' },
  { id: 'contact',    label: 'Contact' },
];

function Navbar({ active }) {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollTo = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      {menuOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 997, background: 'rgba(0,0,0,.5)' }}
          onClick={() => setMenuOpen(false)}
        />
      )}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <button className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="logo-pkg">com.vivek.</span><span className="logo-class">Portfolio</span>
          </button>
          <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            <span/><span/><span/>
          </button>
          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            {NAV_ITEMS.map(({ id, label }) => (
              <button key={id} className={`nav-link ${active === id ? 'active' : ''}`} onClick={() => scrollTo(id)}>
                {label}
              </button>
            ))}
            <a href="/resume.pdf" target="_blank" rel="noreferrer" className="nav-resume">Resume</a>
          </div>
        </div>
      </nav>
    </>
  );
}

/* ─── Hero Code Window ────────────────────────────────────────────────────────── */

function HeroCode() {
  return (
    <div className="hero-code-win">
      <div className="win-bar">
        <span className="win-dot r"/><span className="win-dot y"/><span className="win-dot g"/>
        <span className="win-title">VivekPortfolio.java</span>
      </div>
      <div className="win-body">
        <CLine><Ann>@SpringBootApplication</Ann></CLine>
        <CLine><Ann>@BackendEngineer</Ann></CLine>
        <CLine><Kw>public class </Kw><Cls>VivekPortfolio</Cls> {'{'}</CLine>
        <CLine/>
        <CLine>{'  '}<Kw>public static void </Kw><Fn>main</Fn>(<Cls>String</Cls>[] args) {'{'}</CLine>
        <CLine>{'    '}<Cls>SpringApplication</Cls>.<Fn>run</Fn>(</CLine>
        <CLine>{'      '}<Cls>VivekPortfolio</Cls>.class, args</CLine>
        <CLine>{'    '});</CLine>
        <CLine>{'  }'}</CLine>
        <CLine/>
        <CLine>{'  '}<Ann>@Bean</Ann></CLine>
        <CLine>{'  '}<Kw>public </Kw><Cls>Developer</Cls> <Fn>me</Fn>() {'{'}</CLine>
        <CLine>{'    '}<Kw>return </Kw><Cls>Developer</Cls>.builder()</CLine>
        <CLine>{'      '}.<Fn>name</Fn>(<Str>"Vivek Anand Singh"</Str>)</CLine>
        <CLine>{'      '}.<Fn>role</Fn>(<Str>"Backend Engineer"</Str>)</CLine>
        <CLine>{'      '}.<Fn>stack</Fn>(<Str>"Java + Spring Boot"</Str>)</CLine>
        <CLine>{'      '}.<Fn>loves</Fn>(<Str>"Distributed Systems"</Str>)</CLine>
        <CLine>{'      '}.<Fn>build</Fn>();</CLine>
        <CLine>{'  }'}</CLine>
        <CLine>{'}'}</CLine>
      </div>
    </div>
  );
}

/* ─── Hero ────────────────────────────────────────────────────────────────────── */

function Hero() {
  const typed = useTyping(TYPING_PHRASES);

  return (
    <section className="hero" id="hero">
      <div className="hero-bg"/>
      <div className="hero-glow glow-1"/>
      <div className="hero-glow glow-2"/>
      <div className="hero-inner">
        <div>
          <div className="hero-status">
            <span className="status-dot"/>
            Open to new opportunities
          </div>
          <p className="hero-greeting">@BackendEngineer</p>
          <h1 className="hero-name">Vivek Anand Singh</h1>
          <p className="hero-role">
            <span className="typed">{typed}</span>
            <span className="cursor-blink">|</span>
          </p>
          <p className="hero-desc">
            Building scalable backends, distributed infrastructure,
            and AI-powered systems — primarily with Java &amp; Spring Boot.
          </p>
          <div className="hero-btns">
            <button className="btn-primary" onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}>
              View My Work
            </button>
            <a href="/resume.pdf" target="_blank" rel="noreferrer" className="btn-outline">
              Download Resume
            </a>
          </div>
          <div className="hero-socials">
            <a href={SOCIAL.github}            target="_blank" rel="noreferrer" title="GitHub">   <GitHubIcon/>   </a>
            <a href={SOCIAL.linkedin}           target="_blank" rel="noreferrer" title="LinkedIn"> <LinkedInIcon/> </a>
            <a href={SOCIAL.leetcode}           target="_blank" rel="noreferrer" title="LeetCode"> <LeetCodeIcon/> </a>
            <a href={`mailto:${SOCIAL.email}`}                                  title="Email">    <EmailIcon/>    </a>
          </div>
        </div>
        <HeroCode/>
      </div>
    </section>
  );
}

/* ─── Section Header ──────────────────────────────────────────────────────────── */

function SectionHeader({ num, title }) {
  return (
    <div className="section-header">
      <span className="section-num">{num}.</span>
      <h2 className="section-heading">{title}</h2>
      <div className="section-line"/>
    </div>
  );
}

/* ─── About ───────────────────────────────────────────────────────────────────── */

function About() {
  const [ref, visible] = useReveal();
  return (
    <section className="section" id="about" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="01" title="About"/>
        <div className="about-grid">
          <div className="about-text">
            <p>
              I'm a <span className="hl">Computer Science undergraduate</span> at Scaler School of Technology &amp; BITS Pilani,
              passionate about backend engineering, distributed systems, and AI tooling.
            </p>
            <p>
              I thrive in building <span className="hl">high-performance, scalable systems</span> — from
              microservices architectures with Spring Boot and Kafka, to distributed caches, to RL
              environments powering LLM agents for OpenAI, Meta AI, and Grok AI.
            </p>
            <p>
              My go-to stack is Java + Spring Boot + Kafka. When I'm not writing Java, I'm grinding
              DSA on LeetCode, contributing to open source, or exploring AI infrastructure.
            </p>
            <div className="about-stats">
              <div className="stat"><span className="stat-num">4+</span><span className="stat-label">Internships</span></div>
              <div className="stat"><span className="stat-num">3+</span><span className="stat-label">Major Projects</span></div>
              <div className="stat"><span className="stat-num">8.52</span><span className="stat-label">CGR at SST</span></div>
              <div className="stat"><span className="stat-num">800</span><span className="stat-label">Meta Hackathon Rank</span></div>
            </div>
          </div>
          <div className="code-win">
            <div className="code-bar">
              <span className="code-dot r"/><span className="code-dot y"/><span className="code-dot g"/>
              <span className="code-fname">Vivek.java</span>
            </div>
            <pre className="code-pre">{`@Component
@BackendEngineer
public class Vivek {

  @Value("Bengaluru, Karnataka")
  private String location;

  @Value("2023 - 2027")
  private String studyPeriod;

  private List<String> passions = List.of(
    "Distributed Systems",
    "Java + Spring Boot",
    "Microservices & Kafka",
    "LLM Tooling & MCP",
    "Open Source"
  );

  @Bean
  public String getFavoriteStack() {
    return "Java + Spring Boot + Kafka";
  }

  @Override
  public String toString() {
    return "Backend Engineer. Let's build.";
  }
}`}</pre>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Experience ──────────────────────────────────────────────────────────────── */

function Experience() {
  const [ref, visible] = useReveal();
  return (
    <section className="section section-alt" id="experience" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="02" title="Experience"/>
        <div className="timeline">
          {EXPERIENCES.map((exp, i) => (
            <div className="timeline-item" key={i}>
              <div className="timeline-dot"/>
              <div className="exp-card">
                <div className="exp-header">
                  <div>
                    <h3 className="exp-company">{exp.company}</h3>
                    <p className="exp-role">{exp.role}</p>
                    <p className="exp-ann">{exp.ann}</p>
                  </div>
                  <div className="exp-meta">
                    <span className="exp-period">{exp.period}</span>
                    <span className="exp-location">{exp.location}</span>
                    <span className="exp-tag">{exp.tag}</span>
                  </div>
                </div>
                <ul className="exp-points">
                  {exp.points.map((pt, j) => <li key={j}>{pt}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Projects ────────────────────────────────────────────────────────────────── */

function Projects() {
  const [ref, visible] = useReveal();
  return (
    <section className="section" id="projects" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="03" title="Projects"/>
        <div className="projects-grid">
          {PROJECTS.map((proj, i) => (
            <div className="project-card" key={i}>
              <div>
                <p className="proj-ann">{proj.ann}</p>
                <h3 className="proj-name">{proj.name}</h3>
              </div>
              <ul className="proj-points">
                {proj.points.map((pt, j) => <li key={j}>{pt}</li>)}
              </ul>
              <div className="proj-footer">
                <div className="proj-tech">
                  {proj.tech.map(t => <span key={t} className="tech-tag">{t}</span>)}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  {proj.github && (
                    <a href={proj.github} target="_blank" rel="noreferrer" className="proj-gh" title="GitHub">
                      <GitHubIcon size={15}/>
                    </a>
                  )}
                  {proj.live && (
                    <a href={proj.live} target="_blank" rel="noreferrer" className="proj-gh" title="Live Demo">
                      <ExtIcon/>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Skills ──────────────────────────────────────────────────────────────────── */

function Skills() {
  const [ref, visible] = useReveal();
  return (
    <section className="section section-alt" id="skills" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="04" title="Skills"/>
        <div className="skills-grid">
          {Object.entries(SKILLS).map(([cat, { icon, items }]) => (
            <div className="skill-group" key={cat}>
              <h3 className="skill-cat">
                <span>{icon}</span>
                <span>@{cat}</span>
              </h3>
              <div className="skill-tags">
                {items.map(s => (
                  <span key={s} className={`skill-tag ${s === 'Java' || s === 'Spring Boot' ? 'primary' : ''}`}>{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Education + Achievements ────────────────────────────────────────────────── */

function Education() {
  const [ref, visible] = useReveal();
  return (
    <section className="section" id="education" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="05" title="Education"/>
        <div className="edu-grid">
          {EDUCATION.map((e, i) => (
            <div className="edu-card" key={i}>
              <div className="edu-icon">🎓</div>
              <h3 className="edu-inst">{e.institution}</h3>
              <p className="edu-degree">{e.degree}</p>
              <div className="edu-meta">
                <span className="edu-period">{e.period}</span>
                <span className="edu-score">{e.score}</span>
              </div>
              <p className="edu-loc">{'// '}{e.loc}</p>
            </div>
          ))}
        </div>

        <p className="ach-subheading">Achievements</p>
        <div className="ach-grid">
          {ACHIEVEMENTS.map((a, i) => (
            <div className="ach-card" key={i}>
              <span className="ach-icon">{a.icon}</span>
              <div>
                <h4 className="ach-title">{a.title}</h4>
                <p className="ach-desc">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Contact ─────────────────────────────────────────────────────────────────── */

function Contact() {
  const [ref, visible] = useReveal();
  return (
    <section className="section section-alt" id="contact" ref={ref}>
      <div className={`container reveal ${visible ? 'visible' : ''}`}>
        <SectionHeader num="06" title="Contact"/>
        <p className="contact-tagline">
          Looking to collaborate, hire, or just talk about backend systems?<br/>
          Hit any endpoint below — response time is fast.
        </p>
        <div className="endpoint-box">
          <div className="endpoint">
            <span className="http-get">GET</span>
            <span className="ep-path">"/email"</span>
            <span className="ep-arrow">→</span>
            <a href={`mailto:${SOCIAL.email}`} className="ep-link">{SOCIAL.email}</a>
          </div>
          <div className="endpoint">
            <span className="http-get">GET</span>
            <span className="ep-path">"/linkedin"</span>
            <span className="ep-arrow">→</span>
            <a href={SOCIAL.linkedin} target="_blank" rel="noreferrer" className="ep-link">linkedin.com/in/vivekanandsingh</a>
          </div>
          <div className="endpoint">
            <span className="http-get">GET</span>
            <span className="ep-path">"/github"</span>
            <span className="ep-arrow">→</span>
            <a href={SOCIAL.github} target="_blank" rel="noreferrer" className="ep-link">github.com/vivek-anand-singh</a>
          </div>
          <div className="endpoint">
            <span className="http-get">GET</span>
            <span className="ep-path">"/leetcode"</span>
            <span className="ep-arrow">→</span>
            <a href={SOCIAL.leetcode} target="_blank" rel="noreferrer" className="ep-link">leetcode.com/vivek-anand-singh</a>
          </div>
        </div>
        <div className="contact-socials">
          <a href={SOCIAL.github}            target="_blank" rel="noreferrer" className="contact-btn"><GitHubIcon size={16}/>   GitHub</a>
          <a href={SOCIAL.linkedin}          target="_blank" rel="noreferrer" className="contact-btn"><LinkedInIcon size={16}/> LinkedIn</a>
          <a href={SOCIAL.leetcode}          target="_blank" rel="noreferrer" className="contact-btn"><LeetCodeIcon size={16}/> LeetCode</a>
          <a href={`mailto:${SOCIAL.email}`}                                  className="contact-btn"><EmailIcon size={16}/>    Email</a>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-in">
        <span className="footer-txt">{'// Built with React + ☕ Java love — Vivek Anand Singh'}</span>
        <span className="footer-yr">© 2026</span>
      </div>
    </footer>
  );
}

/* ─── App ─────────────────────────────────────────────────────────────────────── */

const NAV_IDS = ['about', 'experience', 'projects', 'skills', 'education', 'contact'];

function App() {
  const active = useScrollSpy(NAV_IDS);
  return (
    <div className="app">
      <Navbar active={active}/>
      <Hero/>
      <About/>
      <Experience/>
      <Projects/>
      <Skills/>
      <Education/>
      <Contact/>
      <Footer/>
    </div>
  );
}

export default App;
